/**
 * The File System Access picker, read-only (DI-2-1 §2.2 entry 3) — and,
 * DI-5-1, read-write for the write-back (`pickWritableDirectory`), asked only
 * when the stored handle cannot be granted read-write at the approve.
 */
import type { DirectoryHandleLike } from "./dropToFiles.js";

type PickerWindow = Window & {
  showDirectoryPicker?: (opts?: {
    mode?: "read" | "readwrite";
    id?: string;
  }) => Promise<DirectoryHandleLike>;
};

export function supportsDirectoryPicker(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (window as PickerWindow).showDirectoryPicker === "function"
  );
}

/**
 * DI-5-3 — can a handle be asked for READ-WRITE access? (`requestPermission`
 * on `FileSystemHandle.prototype` — Chromium only.) A feature probe: it never
 * prompts and never opens a picker.
 */
export function supportsReadwriteHandles(): boolean {
  const ctor = (
    globalThis as {
      FileSystemHandle?: { prototype?: { requestPermission?: unknown } };
    }
  ).FileSystemHandle;
  return typeof ctor?.prototype?.requestPermission === "function";
}

/** Opens the picker; null when the pilot dismisses it. */
export async function pickDirectory(): Promise<DirectoryHandleLike | null> {
  const w = window as PickerWindow;
  if (!w.showDirectoryPicker) return null;
  try {
    return await w.showDirectoryPicker({ mode: "read", id: "aeriosiq-intake" });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return null;
    throw e;
  }
}

/**
 * DI-5-1 — the picker in `readwrite` (the write-back's fallback when the
 * set's folder handle is not stored or not granted). Null when dismissed or
 * when this browser has no picker. Must be called from a user gesture.
 */
export async function pickWritableDirectory(): Promise<DirectoryHandleLike | null> {
  const w = window as PickerWindow;
  if (!w.showDirectoryPicker) return null;
  try {
    return await w.showDirectoryPicker({
      mode: "readwrite",
      id: "aeriosiq-intake",
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return null;
    throw e;
  }
}

/**
 * DI-5-2 — the SkySpecs export's ROOT: a folder the pilot picks (read-write)
 * for the export (`targetFolderName` is created inside it). The drop's
 * handle IS the images folder and File System Access cannot reach its
 * parent, so the root is asked — once per set, inside the Approve click. Its
 * own picker id, so the browser opens it where the last export went. Null
 * when dismissed or when this browser has no picker. Must be called from a
 * user gesture.
 */
export async function pickExportRootDirectory(): Promise<DirectoryHandleLike | null> {
  const w = window as PickerWindow;
  if (!w.showDirectoryPicker) return null;
  try {
    return await w.showDirectoryPicker({
      mode: "readwrite",
      id: "aeriosiq-skyspecs",
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") return null;
    throw e;
  }
}
